import * as React from 'react'
import querystring from 'query-string'
import { AppService } from '@halo/services/app.service'
import { axiosFetch, isAxiosCancellation } from '@halo/_utils/axios'
import { Singleton, Container } from 'typescript-ioc/es5'
import { UserDto } from '@halo/dtos/user.dto'
import { simpleNotification } from '@halo/_utils/notifications'
import { AddUserSuccessMessage } from '@halo/components/user-responses-messages/add-user-success-message'
import { AppState } from '@halo/stores/app.state'
import aqe from '@pushly/aqe'
import { StatusType } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { IUserPermissionsResponse } from '@halo/interfaces/user-permissions'

@Singleton
export class UserService {
    private appService: AppService

    public constructor() {
        this.appService = Container.get(AppService)
    }

    public async fetchById(userId: any, opts: IApiCallOptions = {}): Promise<IApiResponse<UserDto>> {
        let ok = false
        let user: UserDto
        let meta = {}

        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/users/${userId}?${options}`

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            ok = true
            user = UserDto.fromApiResponse(req.data.data)
            meta = req.data.meta
        } catch (_err) {}

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }
        return { ok, data: user, meta }
    }

    public async fetchAll(opts: IApiCallOptions = {}): Promise<IApiResponse<UserDto[]>> {
        const response: IApiResponse<UserDto[]> = {
            ok: false,
            data: [],
            meta: {},
        }

        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/users?${options}`

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            const { data, ...meta } = res.data

            response.ok = true
            response.data = data.map(UserDto.fromApiResponse)
            response.meta = meta
        } catch (_err) {}

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async fetchAllAccountUsers(accountId: number, opts: IApiCallOptions = {}): Promise<IApiResponse<UserDto[]>> {
        let ok = false
        let users: UserDto[] = []
        let meta = {}

        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/accounts/${accountId}/users?${options}`

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            const { data, ...resMeta } = res.data

            ok = true
            users = data.map(UserDto.fromApiResponse)
            meta = resMeta
        } catch (_err) {}

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }
        return { ok, data: users, meta }
    }

    public async fetchAccountUserByUserId(
        userId: number,
        accountId: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<UserDto>> {
        let ok = false
        let user: UserDto
        let meta = {}

        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/accounts/${accountId}/users/${userId}?${options}`

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            ok = true
            user = UserDto.fromApiResponse(req.data.data)
            meta = req.data.meta
        } catch (_err) {}

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }
        return { ok, data: user, meta }
    }

    public async fetchPermissions(
        userId: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IUserPermissionsResponse>> {
        let ok = false
        let meta = {}
        let permissions: IUserPermissionsResponse = {
            accounts: [],
            domains: [],
        }

        const options = querystring.stringify(opts?.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v2/users/${userId}/permissions?${options}`

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const {
                data: { data },
                ...resMeta
            } = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts?.cancellationKey,
            )

            ok = true
            permissions = data ?? permissions
            meta = resMeta
        } catch (_err) {}

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: permissions, meta }
    }

    public async fetchEventLogs(
        userId: number,
        accountId: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<any>> {
        let ok = false
        let logs: any
        let meta = {}

        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/accounts/${accountId}/users/${userId}/event-logs?${options}`

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            const { data, ...resMeta } = req.data
            ok = true
            logs = data
            meta = resMeta
        } catch (_err) {}

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }
        return { ok, data: logs, meta }
    }

    public async addUser(userDto: any, opts: IApiCallOptions = {}, appState: AppState): Promise<IApiResponse<UserDto>> {
        let ok = false
        let user: UserDto
        let meta: any = {}

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/users`

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            let headerOverrides: any = {}

            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: userDto,
                    config: {
                        headers: headerOverrides,
                    },
                },
                opts.cancellationKey,
            )

            ok = true

            const { data: responseData, ...responseMeta } = req.data
            user = UserDto.fromApiResponse(responseData)
            meta = req.data.meta || responseMeta

            simpleNotification('success', <AddUserSuccessMessage responseData={req.data} showMessage={true} />, 0)
        } catch (err) {
            simpleNotification('error', err.response?.data?.message || 'An error has occurred.')
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }
        return { ok, data: user, meta }
    }

    public async updateUserById(
        userId: number,
        update: any,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<UserDto>> {
        let ok = false
        let user: UserDto
        let meta = {}

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/users/${userId}`

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: update,
                },
                opts.cancellationKey,
            )

            ok = true
            user = UserDto.fromApiResponse(req.data.data)
            meta = req.data.meta
        } catch (_err) {
            if (!isAxiosCancellation(_err)) {
                if (_err?.response?.data?.message) {
                    simpleNotification('error', _err.response.data?.details ?? _err.response.data.message)
                } else {
                    simpleNotification('error', 'The user could not be updated at this time.')
                }
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }
        return { ok, data: user, meta }
    }

    public async triggerInviteForUserId(
        userId: number,
        accountId: number,
        opts: IApiCallOptions = {},
        appState: AppState,
    ): Promise<IApiResponse<UserDto>> {
        let ok = false
        let user: UserDto
        let meta: any = {}

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            let headerOverrides: any = {}

            const req = await axiosFetch('patch', {
                url: `${aqe.defaults.publicApiDomain}/v4/users/${userId}/re-invite`,
                data: {
                    accountId,
                },
                config: {
                    headers: headerOverrides,
                },
            })

            ok = true

            const { data: responseData, ...responseMeta } = req.data
            user = UserDto.fromApiResponse(responseData)
            meta = req.data.meta || responseMeta

            simpleNotification('success', 'An email with the invitation link has been sent to the user.', 0)
        } catch (_err) {
            simpleNotification('error', 'We could not re-process the invitation at this time')
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }
        return { ok, data: user, meta }
    }

    public async triggerPasswordResetForUserId(
        userId: number,
        accountId: number,
        opts: IApiCallOptions = {},
        appState: AppState,
    ): Promise<IApiResponse<UserDto>> {
        let ok = false
        let user: UserDto
        let meta: any = {}

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            let headerOverrides: any = {}

            const req = await axiosFetch('patch', {
                url: `${aqe.defaults.publicApiDomain}/v4/users/${userId}/reset-password`,
                data: {
                    accountId,
                },
                config: {
                    headers: headerOverrides,
                },
            })

            ok = true

            const { data: responseData, ...responseMeta } = req.data
            user = UserDto.fromApiResponse(responseData)
            meta = req.data.meta || responseMeta

            simpleNotification(
                'success',
                <AddUserSuccessMessage
                    type="password"
                    message="User has been sent a password reset email"
                    showMessage={true}
                    responseData={req.data}
                />,
                0,
            )
        } catch (_err) {
            simpleNotification('error', "We could not reset the user's password at this time")
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }
        return { ok, data: user, meta }
    }
}
